// Save/load orchestration for the catalog-driven scoring profile authoring flow. Reads/writes
// filters via the catalog key-value wire format (`scoringProfilesFilters`) against the
// `/api/scoringProfiles/*` endpoints.
// Name/description/coverage/scoring-definition state is owned by the `scoringProfilesCommon`,
// `scoringProfilesDefinition` and `scoringProfilesCoverageDetail` modules — this module only
// builds the `filters` slice of the payload and calls the endpoints.
import { locale } from '../../../imports/i18n'
import { get as _get, post as _post } from '../../../utils/fetcher'
import { mapScoringProfileDefinition } from '../../../utils/drivers'
import { INIT_DEFINITION_SETTINGS } from '../../../settings/scoringProfilesSettings'
import { cloneObject } from '../../../imports/lib'

export default {
  namespaced: true,

  actions: {
    // Live coverage preview while the user edits filters: posts the catalog filters wire format to
    // the `/api/scoringProfiles/coverage` endpoint.
    loadProfileCoverage({ commit, rootGetters }) {
      if (rootGetters['scoringProfilesCommon/getIsProfileCoverageProvidedByAPI']) {
        return
      }

      const objToSend = {
        scoringProfileId: rootGetters['scoringProfilesCommon/getScoringProfileId'],
        filters: rootGetters['scoringProfilesFilters/filtersPayload'],
        productsToExclude: rootGetters['scoringProfilesCoverageDetail/getCoverageExcludedProducts'],
      }

      return _post('/api/scoringProfiles/coverage', objToSend)
        .then((data) => {
          commit('scoringProfilesCommon/setProfileCoverage', data, { root: true })
        })
        .catch((err) => console.log(err))
    },

    // Coverage-detail products list (the "included/excluded products" tab): posts the catalog
    // filters wire format to the `/api/scoringProfiles/coverageDetails` endpoint (`filters` key).
    // Post-processing/mutation is done via a cross-module dispatch to
    // `scoringProfilesCoverageDetail/setUpProductsList`.
    loadProductsList({ dispatch, rootGetters }, scoringProfileId) {
      dispatch('scoringProfilesCommon/setLoading', true, { root: true })

      const objToSend = {
        scoringProfileId: scoringProfileId,
        filters: rootGetters['scoringProfilesFilters/filtersPayload'],
        productsToExclude: rootGetters['scoringProfilesCoverageDetail/getCoverageExcludedProducts'],
      }

      return _post('/api/scoringProfiles/coverageDetails', objToSend)
        .then((data) => {
          let productsToSave = []
          let excludedProducts = []

          if (Array.isArray(data) && data.length === 0) {
            return dispatch('scoringProfilesCoverageDetail/setUpProductsList', { productsToSave, excludedProducts }, { root: true })
          }

          // 204 - no content returns an empty object
          if (typeof data === 'object' && !Array.isArray(data) && data !== null) {
            return dispatch('scoringProfilesCoverageDetail/setUpProductsList', { productsToSave, excludedProducts }, { root: true })
          }

          data.forEach((el) => {
            //Set up excluded products
            if (el.isExcludedFromPerimeter) {
              excludedProducts.push({ issuerCode: el.issuerCode, productName: el.productName })
            }

            //Set up products list
            productsToSave.push({
              productId: el.productId,
              issuerCode: el.issuerCode,
              issuerName: el.issuerName,
              productName: el.productName,
              scoringProfile: el.associatedScoringProfileName,
              isUnderTesting: el.isUnderTesting,
              isFollowed: el.isFollowed,
              isExcludedFromPerimeter: el.isExcludedFromPerimeter,
              name: el.productName,
            })
          })

          return dispatch('scoringProfilesCoverageDetail/setUpProductsList', { productsToSave, excludedProducts }, { root: true })
        })
        .finally(() => {
          dispatch('scoringProfilesCommon/setLoading', false, { root: true })
        })
    },

    saveScoringProfile({ dispatch, rootGetters }) {
      dispatch('scoringProfilesCommon/setLoading', true, { root: true })

      const profile = {
        scoringProfileId: rootGetters['scoringProfilesCommon/getScoringProfileId'],
        scoringProfileName: rootGetters['scoringProfilesCommon/getScoringProfileName'],
        scoringProfileDescription: rootGetters['scoringProfilesCommon/getScoringProfileDescription'],
        filters: rootGetters['scoringProfilesFilters/filtersPayload'],
        scoringDefinition: rootGetters['scoringProfilesDefinition/getScoringDefinitionToSendToAPI'],
        scoringDefinitionWithModel: {
          scoringProfileModelId: rootGetters['scoringProfilesDefinition/getSelectedModelId'],
          scoringProfileModelName: rootGetters['scoringProfilesDefinition/getSelectedModelName'],
          driversSorting: rootGetters['scoringProfilesDefinition/getSortedModelDriversToSendToAPI'],
        },
        productsToExclude: rootGetters['scoringProfilesCoverageDetail/getCoverageExcludedProducts'],
      }

      return _post('/api/scoringProfiles/save', profile).finally(() => {
        dispatch('scoringProfilesCommon/setLoading', false, { root: true })
      })
    },

    loadScoringProfile({ commit, dispatch, rootGetters }, params) {
      dispatch('scoringProfilesCommon/setLoading', true, { root: true })
      commit('scoringProfilesCommon/setIsProfileCoverageProvidedByAPI', true, { root: true })
      dispatch('scoringProfilesDefinition/resetSelectedModel', null, { root: true })

      return _get(`/api/scoringProfiles/${params.id}`)
        .then((data) => {
          let id = params.id
          let name = data.scoringProfileName
          if (params.action === 'duplicate') {
            id = null
            name = data.scoringProfileName + ' - ' + (locale() === 'it' ? 'Copia' : 'Copy')
            dispatch('scoringProfilesCommon/setIsDuplicate', true, { root: true })
          }
          dispatch('scoringProfilesCommon/setScoringProfileId', id, { root: true })
          dispatch('scoringProfilesCommon/setScoringProfileName', name, { root: true })
          dispatch('scoringProfilesCommon/setScoringProfileDescription', data.scoringProfileDescription || '', {
            root: true,
          })

          dispatch('scoringProfilesFilters/setFilters', data.filters || [], { root: true })

          dispatch(
            'scoringProfilesCoverageDetail/setDefaultExcludedProducts',
            data.productsToExclude,
            { root: true }
          )
          dispatch(
            'scoringProfilesCoverageDetail/setUpProductsList',
            { excludedProducts: data.productsToExclude, productsToSave: [] },
            { root: true }
          )

          dispatch('scoringProfilesDefinition/setSelectedWeightType', data.scoringDefinition.weightType, {
            root: true,
          })
          dispatch(
            'scoringProfilesDefinition/setDefaultSelectedWeightType',
            data.scoringDefinition.weightType,
            { root: true }
          )
          dispatch(
            'scoringProfilesDefinition/setSequenceWeightsDecay',
            data.scoringDefinition.geometricDrivers.sequenceWeightsDecay,
            { root: true }
          )
          dispatch(
            'scoringProfilesDefinition/setDefaultSequenceWeightsDecay',
            data.scoringDefinition.geometricDrivers.sequenceWeightsDecay,
            { root: true }
          )

          const scoringDefinitionSettings = mapScoringProfileDefinition(
            INIT_DEFINITION_SETTINGS(),
            data.scoringDefinition
          )

          dispatch('scoringProfilesDefinition/setScoringDefinitionSettings', scoringDefinitionSettings, {
            root: true,
          })
          dispatch(
            'scoringProfilesDefinition/setDefaultScoringDefinitionSettings',
            cloneObject(scoringDefinitionSettings),
            { root: true }
          )

          if (data.scoringDefinitionWithModel.scoringProfileModelId) {
            dispatch(
              'scoringProfilesDefinition/setDefaultSelectedModelId',
              data.scoringDefinitionWithModel.scoringProfileModelId,
              { root: true }
            )
            dispatch(
              'scoringProfilesDefinition/setSelectedModelId',
              data.scoringDefinitionWithModel.scoringProfileModelId,
              { root: true }
            )
            dispatch(
              'scoringProfilesDefinition/setScoringProfileDefinitionSelectValue',
              data.scoringDefinitionWithModel.scoringProfileModelId,
              { root: true }
            )
            dispatch(
              'scoringProfilesDefinition/setSelectedModelName',
              data.scoringDefinitionWithModel.scoringProfileModelName,
              { root: true }
            )
            dispatch(
              'scoringProfilesDefinition/setSortedModelDriversFromAPI',
              data.scoringDefinitionWithModel.driversSorting,
              { root: true }
            )
          } else {
            dispatch('scoringProfilesDefinition/setScoringProfileDefinitionSelectValue', 0, { root: true })
          }

          commit('scoringProfilesCommon/setProfileCoverage', data.profileCoverage, { root: true })
        })
        .finally(() => {
          dispatch('scoringProfilesCommon/setLoading', false, { root: true })
          commit('scoringProfilesCommon/setIsProfileCoverageProvidedByAPI', false, { root: true })
        })
    },
  },
}
